/**
 * OpenHouseMap.jsx — Shared Google Map for the Open House Planner
 * (Browse + Plan tabs). Colored circle pins with short labels, InfoWindow
 * on click, optional route polyline, auto-fitBounds on marker set changes.
 *
 * props:
 *   markers  — [{id, lat, lng, label, color, title, subtitle, url}]
 *   path     — [{lat, lng}] | null (route polyline)
 *   selectedId — highlighted pin (bigger); map pans to it on change
 *   onMarkerClick(id)
 *   height   — px (default 480)
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Alert, CircularProgress, Link } from '@mui/material';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Polyline } from '@react-google-maps/api';

const MAPS_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_KEY;
const LIBRARIES = ['places'];

const SB_CENTER = { lat: 34.4208, lng: -119.6982 };
const ROUTE_COLOR = '#E879F9';

export default function OpenHouseMap({
  markers = [],
  path = null,
  selectedId,
  onMarkerClick,
  height = 480,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'rfos-google-maps',
    googleMapsApiKey: MAPS_API_KEY || '',
    libraries: LIBRARIES,
  });

  const mapRef = useRef(null);
  const [infoId, setInfoId] = useState(null);

  const pins = useMemo(
    () => (markers || [])
      .filter((m) => m.lat != null && m.lng != null)
      .map((m) => ({ ...m, lat: parseFloat(m.lat), lng: parseFloat(m.lng) })),
    [markers],
  );

  // Refit bounds only when the set of pinned ids changes (not on every render)
  const boundsKey = useMemo(() => pins.map((m) => m.id).sort().join('|'), [pins]);
  useEffect(() => {
    if (!isLoaded || !mapRef.current || pins.length === 0) return;
    if (pins.length === 1) {
      mapRef.current.panTo({ lat: pins[0].lat, lng: pins[0].lng });
      mapRef.current.setZoom(14);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    pins.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
    mapRef.current.fitBounds(bounds, 48);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, boundsKey]);

  // Pan to the externally-selected pin (e.g. list card click)
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !selectedId) return;
    const m = pins.find((p) => p.id === selectedId);
    if (m) mapRef.current.panTo({ lat: m.lat, lng: m.lng });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, selectedId]);

  const handleMapLoad = (map) => { mapRef.current = map; };

  const infoPin = infoId ? pins.find((p) => p.id === infoId) : null;

  if (!MAPS_API_KEY) {
    return (
      <Alert severity="error">Google Maps API key missing (VITE_GOOGLE_PLACES_KEY).</Alert>
    );
  }
  if (loadError) {
    return (
      <Alert severity="error">Failed to load Google Maps: {loadError.message}</Alert>
    );
  }
  if (!isLoaded) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', borderRadius: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={SB_CENTER}
        zoom={12}
        onLoad={handleMapLoad}
        onClick={() => setInfoId(null)}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          clickableIcons: false,
        }}
      >
        {pins.map((m) => {
          const isSelected = m.id === selectedId;
          return (
            <Marker
              key={m.id}
              position={{ lat: m.lat, lng: m.lng }}
              zIndex={isSelected ? 1000 : 1}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: isSelected ? 18 : 14,
                fillColor: m.color || '#3B82F6',
                fillOpacity: 1,
                strokeColor: '#FFFFFF',
                strokeWeight: isSelected ? 3 : 2,
              }}
              label={m.label ? {
                text: String(m.label),
                color: '#FFFFFF',
                fontWeight: '700',
                fontSize: isSelected ? '12px' : '11px',
              } : undefined}
              onClick={() => {
                setInfoId(m.id);
                onMarkerClick?.(m.id);
              }}
            />
          );
        })}

        {infoPin && (
          <InfoWindow
            position={{ lat: infoPin.lat, lng: infoPin.lng }}
            onCloseClick={() => setInfoId(null)}
            options={{ pixelOffset: new window.google.maps.Size(0, -16) }}
          >
            <Box sx={{ maxWidth: 240, pr: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1A1A2E' }}>
                {infoPin.title || ''}
              </Typography>
              {infoPin.subtitle && (
                <Typography variant="caption" sx={{ display: 'block', color: '#667085' }}>
                  {infoPin.subtitle}
                </Typography>
              )}
              {infoPin.url && (
                <Link
                  href={infoPin.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  sx={{ display: 'inline-block', mt: 0.5, fontWeight: 600 }}
                >
                  View listing ↗
                </Link>
              )}
            </Box>
          </InfoWindow>
        )}

        {path && path.length >= 2 && (
          <Polyline
            path={path}
            options={{
              strokeColor: ROUTE_COLOR,
              strokeWeight: 4,
              strokeOpacity: 0.8,
            }}
          />
        )}
      </GoogleMap>
    </Box>
  );
}
